/**
 * React hook surface for the map-dimension policy. The pure computation
 * lives in ./mapDimensions.ts so it can be unit-tested without an RN
 * runtime; this file is the thin wrapper that reads useWindowDimensions
 * and delegates.
 *
 * Layout policy (also documented in mapDimensions.ts):
 *   - Phone PORTRAIT:  action panel below the map; map height clamped
 *                      so the bottom panel always has its 220 px reserve.
 *   - Phone LANDSCAPE: left sidebar for the action panel; map controls
 *                      live in the bottom bar (no longer overlaying the map).
 *   - DESKTOP:         380 px left sidebar; 48 px right cog gutter; map
 *                      wrapper shrinks to svgWidth (no dead ocean).
 */

import { Platform } from 'react-native'
import { useStableWindowDimensions } from '../hooks/useStableWindowDimensions'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import type { GameState } from './gameState'
import {
  computeMapDimensions,
  DEFAULT_RESERVED_LEFT_LANDSCAPE,
  DEFAULT_RESERVED_LEFT_DESKTOP,
  DEFAULT_RESERVED_RIGHT_LANDSCAPE,
} from './mapDimensions'
import type { MapDimensions } from './mapDimensions'

export type { MapDimensions } from './mapDimensions'
export {
  computeMapDimensions,
  DEFAULT_RESERVED_LEFT_LANDSCAPE,
  DEFAULT_RESERVED_LEFT_DESKTOP,
  DEFAULT_RESERVED_RIGHT_LANDSCAPE,
} from './mapDimensions'

export function useMapDimensions(
  state: GameState,
  opts: {
    reservedTop?: number
    reservedBottom?: number
    reservedLeft?: number
    reservedRight?: number
    /** Portrait: blank viewBox margins to cut (see computeMapDimensions). */
    trimTop?: number
    trimBottom?: number
    /**
     * Landscape notch clearance. Defaults to true: the map box fills the pane
     * to the physical screen edge (game screen), so it needs the horizontal
     * safe insets subtracted to clear the notch. Pass FALSE when the map is
     * already inside a safe-area-padded container (one that pads via its own
     * SafeAreaProvider) — applying it there would double-inset and letterbox
     * the map.
     */
    applyNotchInset?: boolean
  } = {},
): MapDimensions {
  // Keyboard-stable: an Android soft keyboard resizes the window, which used
  // to shrink the map (and lift the controls bar) when chat was focused.
  const { width: screenWidth, height: screenHeight } = useStableWindowDimensions()
  const insets = useSafeAreaInsets()
  // No extraPaddingFactor: iOS Safari landscape used to get +8% viewBox
  // padding so the corner overlay chips (cog/regions/zoom/log) had ocean to
  // sit on instead of covering territories. Those controls now live in the
  // bottom bar, not over the map, so the map should fill the space like
  // every other platform. (computeMapDimensions still supports the param for
  // future callers; the hook just no longer supplies one.)
  const dims = computeMapDimensions(screenWidth, screenHeight, state.map.viewBox, opts)

  // Landscape notch clearance (native). The map box fills the map pane, whose
  // edge is the physical screen edge — so on a notched / Dynamic-Island iPhone
  // the map (and anything sized to it, e.g. the bottom controls bar) rendered
  // UNDER the sensor housing on whichever side it landed. Those devices report
  // SYMMETRIC landscape insets (left == right — iOS won't tell you which side
  // the island is on), and the box is centred in the pane, so subtracting
  // (left + right) from the rendered width pulls it in by one inset on EACH
  // side, clearing the notch in both rotations. Applied here in the hook so
  // every consumer (MapView, MapControlsBarSlot, …) shrinks consistently and
  // stays aligned. Portrait insets are 0 → no-op.
  if (Platform.OS !== 'web' && dims.isLandscape && opts.applyNotchInset !== false) {
    const hInset = insets.left + insets.right
    if (hInset > 0) {
      return {
        ...dims,
        svgWidth: Math.max(120, dims.svgWidth - hInset),
        containerWidth: Math.max(120, dims.containerWidth - hInset),
      }
    }
  }
  return dims
}

